import React from 'react';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Privacy Policy for Omniosocial
        </h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: June 2026</p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Introduction</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            Omniosocial is a web-based platform that enables users to retrieve, view, store, and
            review publicly available Facebook Page content and related Page information through
            the Facebook Graph API.
          </p>
          <p className="text-gray-600 leading-relaxed">
            This Privacy Policy explains how we collect, process, store, and use information
            obtained through Meta&apos;s Facebook Graph API and how users may contact us regarding
            privacy-related matters.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Information We Access</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            Omniosocial accesses publicly available Facebook Page information through the Facebook
            Graph API, including:
          </p>
          <ul className="list-disc list-outside pl-6 space-y-1 text-gray-600 mb-3">
            <li>Public Facebook Page posts</li>
            <li>Public Page metadata</li>
            <li>Public follower counts and related Page information</li>
            <li>Other publicly available Page content permitted by Meta Platform policies</li>
          </ul>
          <p className="text-gray-600 leading-relaxed">
            Omniosocial does not access private Facebook user content, private messages,
            non-public Facebook information, or personal Facebook account data.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Information Processing</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            Information obtained through the Facebook Graph API is processed solely to provide
            the functionality of the Omniosocial platform.
          </p>
          <p className="text-gray-600 leading-relaxed mb-3">
            The platform retrieves publicly available Facebook Page content and displays it within
            the Omniosocial dashboard for authenticated users.
          </p>
          <p className="text-gray-600 leading-relaxed">
            All processing is performed in accordance with applicable laws, regulations, Meta
            Platform Terms, and Meta Developer Policies.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">How We Use Information</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            The information obtained through the Facebook Graph API is used to:
          </p>
          <ul className="list-disc list-outside pl-6 space-y-1 text-gray-600">
            <li>Retrieve publicly available Facebook Page content</li>
            <li>Display Facebook Page posts and related information within the platform</li>
            <li>Store retrieved public Page content for platform functionality</li>
            <li>Enable users to review and organize publicly available Facebook Page content</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Information Sharing</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            Omniosocial does not sell Facebook data.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Information obtained through the Facebook Graph API is not shared with third parties
            except where required by law, regulation, legal process, or as necessary to operate
            and secure our services.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Data Retention</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            Public Facebook Page content retrieved through the Facebook Graph API may be stored
            within Omniosocial systems for operational and platform purposes.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Data is retained only for as long as reasonably necessary to provide the service and
            meet legal, regulatory, and contractual obligations.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Data Deletion Requests</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            If you believe Omniosocial has collected information relating to you and would like it
            removed, you may submit a deletion request by contacting:
          </p>
          <p className="mb-3">
            <a className="text-blue-600 underline" href="mailto:team@omniosocial.com">
              team@omniosocial.com
            </a>
          </p>
          <p className="text-gray-600 leading-relaxed mb-3">
            Please provide sufficient information to help us identify the relevant records.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Upon receiving a valid request, we will review the request and, where appropriate,
            remove the applicable information within a reasonable timeframe in accordance with
            Meta Platform requirements and applicable law.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Security</h2>
          <p className="text-gray-600 leading-relaxed">
            We implement reasonable technical and organizational measures designed to protect
            information processed through our platform from unauthorized access, disclosure,
            alteration, or destruction.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Consistency with Meta Terms</h2>
          <p className="text-gray-600 leading-relaxed">
            This Privacy Policy does not supersede, modify, or conflict with Meta&apos;s Terms,
            Developer Policies, Platform Terms, or any other applicable Meta policies.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Public Availability</h2>
          <p className="text-gray-600 leading-relaxed">
            This Privacy Policy is publicly available on our website and is accessible to users
            of the Omniosocial platform.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Updates to This Policy</h2>
          <p className="text-gray-600 leading-relaxed">
            We may update this Privacy Policy from time to time. Any changes will be published on
            this page. Continued use of the platform after updates constitutes acceptance of the
            revised policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Contact Us</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            If you have any questions regarding this Privacy Policy or our use of Facebook
            Platform data, please contact:
          </p>
          <p>
            <a className="text-blue-600 underline" href="mailto:team@omniosocial.com">
              team@omniosocial.com
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}

export default PrivacyPolicy;
